/**
 * AI Decision Engine
 * Provides basic AI decision-making for automated players
 * Treats AI as just another player that acts programmatically
 */
export class AIDecisionEngine {
    /**
     * Get the next action for an AI player
     * Simple heuristic-based strategy:
     * 1. Play cards if we have magicka
     * 2. Attack creatures (prioritize guards, then weak targets)
     * 3. Attack player if no guards blocking
     * 4. End turn if no productive moves
     */
    getNextAction(gameState, playerId) {
        const player = gameState.players.find(p => p.id === playerId);
        const opponent = gameState.players.find(p => p.id !== playerId);
        if (!player || !opponent) {
            return { type: 'end-turn' };
        }
        // Try to play cards
        const playableCard = player.hand.find(c => c.type === 'creature' && c.cost <= player.magicka);
        if (playableCard) {
            // Find empty slot
            const playerIndex = gameState.players.findIndex(p => p.id === playerId);
            const playerLane = playerIndex === 0
                ? gameState.board.playerLane
                : gameState.board.opponentLane;
            const emptySlot = playerLane.findIndex(c => c === null);
            if (emptySlot !== -1) {
                return {
                    type: 'play-card',
                    cardId: playableCard.id,
                    position: emptySlot,
                };
            }
        }
        // Try to attack
        const playerIndex = gameState.players.findIndex(p => p.id === playerId);
        const myLane = playerIndex === 0
            ? gameState.board.playerLane
            : gameState.board.opponentLane;
        const opponentLane = playerIndex === 0
            ? gameState.board.opponentLane
            : gameState.board.playerLane;
        // Find attackable creatures
        const myAttackers = myLane.filter(c => c && c.owner === playerId && c.canAttack && !c.hasAttacked);
        // Check for opponent guards
        const opponentGuards = opponentLane.filter(c => c && c.keywords.includes('guard'));
        for (const attacker of myAttackers) {
            // If there are guards, attack them
            if (opponentGuards.length > 0) {
                const guard = opponentGuards[0];
                return {
                    type: 'attack-creature',
                    attackerId: attacker.id,
                    targetId: guard.id,
                };
            }
            // Attack weakest non-guard creatures first
            const weakestCreature = opponentLane
                .filter(c => c && !c.keywords.includes('guard'))
                .sort((a, b) => (a?.currentHealth ?? 999) - (b?.currentHealth ?? 999))[0];
            if (weakestCreature) {
                return {
                    type: 'attack-creature',
                    attackerId: attacker.id,
                    targetId: weakestCreature.id,
                };
            }
            // No creatures to attack, attack player directly
            return {
                type: 'attack-player',
                attackerId: attacker.id,
                targetPlayerId: opponent.id,
            };
        }
        // No more moves, end turn
        return { type: 'end-turn' };
    }
}
